/**
 * You › Providers › Routing (G11 / V31): "image generation → Codex", "Windows/GPU → Photon" …
 * Stated rules beat learned evidence, which beats defaults. What Viko learned from verified
 * outcomes is shown next to the rules. Stored as encrypted settings; the hub never sees them.
 */
import { useQueryClient } from '@tanstack/react-query'
import {
  type RoutingPreferences as Prefs,
  ROUTING_TASK_TYPE_LABEL,
  ROUTING_TASK_TYPES,
  type RoutingTaskType,
} from '@viko/protocol'
import { statsFor } from '@viko/scheduler'
import { Button } from '@viko/transcript-ui'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { useMachines, useProjects } from '../lib/queries.js'
import {
  removeRule,
  saveRoutingPreferences,
  upsertRule,
  useRoutingOutcomes,
  useRoutingPreferences,
} from '../lib/routing-prefs.js'
import { errorText, useToast } from './ui.js'

const AGENT = { claude: 'Claude', codex: 'Codex' } as const

export function RoutingPreferences() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const prefs = useRoutingPreferences()
  const outcomes = useRoutingOutcomes()
  const machines = useMachines()
  const projects = useProjects()
  const [type, setType] = useState<RoutingTaskType>('image-generation')
  const [agent, setAgent] = useState<'' | 'claude' | 'codex'>('codex')
  const [machine, setMachine] = useState('')
  const [projectId, setProjectId] = useState('')
  if (!b.entities)
    return (
      <p className="m-0 px-4 text-[14px] text-text-3">
        Routing preferences need an unlocked, end-to-end encrypted account.
      </p>
    )
  const p = prefs.data
  if (!p) return <p className="m-0 px-4 text-[14px] text-text-3">Loading routing preferences…</p>
  const save = async (next: Prefs) => {
    try {
      const entities = b.entities
      if (!entities) throw new Error('Encrypted account locked')
      const value = await saveRoutingPreferences(entities, next)
      qc.setQueryData(['routing-preferences', b.artifactAccess.accountId], value)
      toast('Routing preferences saved')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const machineName = (ref: string) => machines.data?.find((m) => m.id === ref)?.name ?? ref
  const projectName = (id: string) => projects.data?.find((x) => x.id === id)?.name ?? id
  const learned = ROUTING_TASK_TYPES.flatMap((t) =>
    (['claude', 'codex'] as const).flatMap((a) => {
      const xs = (outcomes.data ?? []).filter((o) => o.taskType === t && o.agent === a)
      return xs.length ? [{ t, a, s: statsFor(xs, Date.now()) }] : []
    }),
  )
  return (
    <div className="mx-4 grid gap-4 text-[14px]" data-testid="routing-preferences">
      <p className="m-0 text-text-2">
        Your rules come first, then what Viko learned from verified outcomes, then defaults.
        Headroom and machine load still apply, and every choice says why.
      </p>
      <ul className="m-0 grid list-none gap-2 p-0" aria-label="Routing rules">
        {p.rules.length === 0 ? <li className="text-text-3">No rules yet.</li> : null}
        {p.rules.map((r) => (
          <li key={r.id} className="card flex flex-wrap items-center gap-x-3 gap-y-1 p-3">
            <span className="font-medium">{ROUTING_TASK_TYPE_LABEL[r.taskType]}</span>
            <span>
              →{' '}
              {[r.agent ? AGENT[r.agent] : null, r.machine ? `on ${machineName(r.machine)}` : null]
                .filter(Boolean)
                .join(' ')}
            </span>
            {r.projectId ? <span className="text-text-3">· {projectName(r.projectId)}</span> : null}
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => save(removeRule(p, r.id))}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="grid gap-3 sm:grid-cols-2"
        aria-label="Add routing rule"
        onSubmit={(e) => {
          e.preventDefault()
          if (!agent && !machine) return
          void save(
            upsertRule(p, {
              id: `${type}-${projectId || 'all'}`,
              taskType: type,
              ...(agent ? { agent } : {}),
              ...(machine ? { machine } : {}),
              ...(projectId ? { projectId } : {}),
            }),
          )
        }}
      >
        <label className="field">
          <span>Task type</span>
          <select value={type} onChange={(e) => setType(e.target.value as RoutingTaskType)}>
            {ROUTING_TASK_TYPES.map((t) => (
              <option key={t} value={t}>
                {ROUTING_TASK_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Prefer agent</span>
          <select value={agent} onChange={(e) => setAgent(e.target.value as typeof agent)}>
            <option value="">No preference</option>
            <option value="claude">Claude</option>
            <option value="codex">Codex</option>
          </select>
        </label>
        <label className="field">
          <span>Prefer machine</span>
          <select value={machine} onChange={(e) => setMachine(e.target.value)}>
            <option value="">No preference</option>
            {(machines.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Applies to</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Every project</option>
            {(projects.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <div>
          <Button type="submit" disabled={!agent && !machine}>
            Save rule
          </Button>
        </div>
      </form>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={p.learn}
          onChange={(e) => save({ ...p, learn: e.target.checked })}
        />
        Learn from verified outcomes when no rule applies
      </label>
      {p.learn ? (
        <section className="grid gap-1" aria-label="Learned performance">
          <h3 className="m-0 text-[14px] font-semibold">Learned so far</h3>
          {learned.length === 0 ? (
            <p className="m-0 text-text-3">
              Nothing yet. Landed and verified runs teach Viko which agent suits which task.
            </p>
          ) : (
            <ul className="m-0 list-none p-0">
              {learned.map(({ t, a, s }) => (
                <li key={`${t}:${a}`} className="tabular-nums">
                  {ROUTING_TASK_TYPE_LABEL[t]} · {AGENT[a]}: {s.successPct}% verified ({s.verified}{' '}
                  of {s.n})
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  )
}
